import { buildApiUrl, buildAuthHeaders } from '@/api/request'
import { logger } from '@/utils/logger'

/**
 * P3-2: Agent v2 SSE 流式客户端 (带 Last-Event-ID 重连与断电恢复)。
 */

export interface SseV2Options {
  fromSeq?: number
  signal?: AbortSignal
  onEvent?: (eventType: string, data: unknown, seq: number) => void
  onError?: (err: Error) => void
  onClose?: () => void
}

export async function fetchSseV2Stream(
  runId: string,
  options: SseV2Options = {}
): Promise<void> {
  let seq = Math.max(1, options.fromSeq || 1)
  let lastDeliveredSeq = seq - 1
  let attempts = 0
  const maxAttempts = 3

  while (attempts < maxAttempts) {
    try {
      const url = buildApiUrl(`/ai/v2/chat/streams/${encodeURIComponent(runId)}/events?fromSeq=${seq}`)
      const headers = new Headers(buildAuthHeaders({ Accept: 'text/event-stream' }))
      if (seq > 1) {
        headers.set('Last-Event-ID', String(seq - 1))
      }

      const response = await fetch(url, { headers, signal: options.signal })
      if (!response.ok || !response.body) {
        throw new Error(`SSE HTTP error: ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''

      let sawTerminalEvent = false
      while (true) {
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        let currentEvent = ''
        let currentId = 0
        let currentData = ''

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed) {
            if (currentEvent && currentData) {
              try {
                const parsed = JSON.parse(currentData)
                const envelope: { event?: unknown; seq?: unknown; data?: unknown } | null =
                  parsed && typeof parsed === 'object'
                    ? (parsed as { event?: unknown; seq?: unknown; data?: unknown })
                    : null
                const resolvedEvent =
                  typeof envelope?.event === 'string' ? envelope.event : currentEvent
                const resolvedSeq = typeof envelope?.seq === 'number'
                  ? envelope.seq
                  : (currentId || seq)
                if (resolvedSeq > lastDeliveredSeq) {
                  if (options.onEvent) {
                    // Keep the server envelope intact so the normal reducer retains runId/seq.
                    options.onEvent(resolvedEvent, parsed, resolvedSeq)
                  }
                  lastDeliveredSeq = resolvedSeq
                  seq = resolvedSeq + 1
                  sawTerminalEvent = resolvedEvent === 'run_done' || resolvedEvent === 'run_error'
                }
              } catch {
                const fallbackSeq = currentId || seq
                if (fallbackSeq > lastDeliveredSeq && options.onEvent) {
                  options.onEvent(currentEvent, { text: currentData }, fallbackSeq)
                  lastDeliveredSeq = fallbackSeq
                  seq = fallbackSeq + 1
                }
              }
            }
            currentEvent = ''
            currentData = ''
            currentId = 0
            continue
          }

          if (trimmed.startsWith('id:')) {
            currentId = parseInt(trimmed.substring(3).trim(), 10) || 0
          } else if (trimmed.startsWith('event:')) {
            currentEvent = trimmed.substring(6).trim()
          } else if (trimmed.startsWith('data:')) {
            currentData += trimmed.substring(5).trim()
          }
        }
      }

      if (sawTerminalEvent) {
        if (options.onClose) options.onClose()
        return
      }
      throw new Error('SSE connection closed before a terminal event')
    } catch (err) {
      if (options.signal?.aborted) {
        throw err
      }
      attempts++
      logger.warn(`[SSE v2] Stream error (attempt ${attempts}/${maxAttempts}): ${err instanceof Error ? err.message : String(err)}`)
      if (attempts >= maxAttempts) {
        if (options.onError) options.onError(err instanceof Error ? err : new Error(String(err)))
        return
      }
      await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempts) * 500))
    }
  }
}

/** Explicit user stop; refresh/network disconnects deliberately do not call this endpoint. */
export async function cancelSseV2Stream(runId: string): Promise<void> {
  const normalized = runId.trim()
  if (!normalized) return
  await fetch(buildApiUrl(`/ai/v2/chat/streams/${encodeURIComponent(normalized)}/cancel`), {
    method: 'POST',
    headers: buildAuthHeaders({ Accept: 'application/json' })
  })
}
